import { OrgFullDetail, OrgMemberRole, OrgMemberWithUser } from "@garther/interfaces";
import { Button, Input, Popup, Select, Table } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { PenLine } from "lucide-react";
import { useState } from "react";

import { orgApiClient } from "@/libs/api/org-client";
import { useAuthStore } from "@/stores/auth.store";

interface OrgMemberProps {
  org: OrgFullDetail;
}

export function OrgMember({ org }: OrgMemberProps) {
  const { formatDate } = useI18n();

  const { curUser } = useAuthStore();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteUserId, setInviteUserId] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgMemberRole>(OrgMemberRole.MEMBER);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // Check if current user is owner of this organization
  const isOwner = org.members.some(
    (member) => member.userId === curUser?.profile?.id && member.role === OrgMemberRole.OWNER
  );

  const handleInviteMember = async () => {
    if (!inviteUserId.trim()) {
      setInviteError("User ID is required");
      return;
    }

    setIsInviting(true);
    setInviteError("");

    try {
      await orgApiClient.updateMemberRole(org.id, {
        userId: inviteUserId.trim(),
        role: inviteRole
      });

      // Close modal and reset form
      setIsInviteModalOpen(false);
      setInviteUserId("");
      setInviteRole(OrgMemberRole.MEMBER);

      // TODO: Refresh the org data to show the new member
      // This would typically involve refetching the org data
    } catch (error) {
      console.error("Failed to update member role:", error);
      setInviteError(error instanceof Error ? error.message : "Failed to update member role");
    } finally {
      setIsInviting(false);
    }
  };

  const roleOptions = [
    { value: OrgMemberRole.MEMBER, label: "Member" },
    { value: OrgMemberRole.ADMIN, label: "Admin" },
    { value: OrgMemberRole.OWNER, label: "Owner" }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-responsive-lg font-normal text-text-primary">Team Members</p>
          <p className="text-responsive-base font-normal text-text-secondary">
            Manage your organization's team members and roles
          </p>
        </div>
        {isOwner && (
          <Button variant="outline" padding="px-4 py-2" onClick={() => setIsInviteModalOpen(true)}>
            Invite Member
          </Button>
        )}
      </div>

      <Table
        data={org.members}
        columns={[
          {
            key: "name",
            title: "Name",
            dataIndex: "user.name"
          },
          {
            key: "role",
            title: "Role",
            dataIndex: "role"
          },
          {
            key: "email",
            title: "Email",
            dataIndex: "user.email"
          },
          {
            key: "phone",
            title: "Phone",
            dataIndex: "user.phone"
          },
          {
            key: "createdAt",
            title: "Created At",
            dataIndex: "user.createdAt",
            formatter: (value: Date) => formatDate(value, { year: "numeric", month: "short", day: "numeric" })
          },
          ...(isOwner
            ? [
                {
                  key: "actions",
                  title: "Actions",
                  render: (_value: any, record: OrgMemberWithUser) => {
                    return (
                      <Button
                        variant="ghost"
                        padding="p-2"
                        onClick={() => {
                          setInviteUserId(record.userId);
                          setInviteRole(record.role);
                          setIsInviteModalOpen(true);
                        }}
                      >
                        <PenLine className="w-4 h-4" />
                      </Button>
                    );
                  }
                }
              ]
            : [])
        ]}
      />

      {/* Invite Member Modal */}
      <Popup
        isOpen={isInviteModalOpen}
        onClose={() => {
          setIsInviteModalOpen(false);
          setInviteUserId("");
          setInviteRole(OrgMemberRole.MEMBER);
          setInviteError("");
        }}
      >
        <div className="space-y-6">
          <div>
            <h2 className="text-responsive-lg font-semibold text-text-primary">Invite Member</h2>
            <p className="text-responsive-base text-text-secondary mt-1">
              Add a new member to your organization by entering their user ID.
            </p>
          </div>

          <div className="space-y-4">
            <Input
              label="User ID"
              placeholder="Enter user ID"
              value={inviteUserId}
              onChange={(e) => setInviteUserId(e.target.value)}
              required
              error={inviteError && !inviteUserId.trim() ? inviteError : undefined}
            />

            <Select
              label="Role"
              options={roleOptions}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as OrgMemberRole)}
              required
            />
          </div>

          {inviteError && inviteUserId.trim() && (
            <div className="text-sm text-red-400 bg-red-50 p-3 rounded-sm">{inviteError}</div>
          )}

          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsInviteModalOpen(false);
                setInviteUserId("");
                setInviteRole(OrgMemberRole.MEMBER);
                setInviteError("");
              }}
              disabled={isInviting}
            >
              Cancel
            </Button>
            <Button onClick={handleInviteMember} disabled={isInviting || !inviteUserId.trim()}>
              {isInviting ? "Inviting..." : "Invite Member"}
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  );
}
